import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["startDate", "endDate", "userCheckbox", "conflictWarnings", "conflictList", "submitButton"]
  static values = {
    projectSlug: String,
    milestoneId: String
  }
  
  connect() {
    this.checkConflicts()
  }
  
  async checkConflicts() {
    // Only check conflicts if we have dates and at least one user selected
    const startDate = this.startDateTarget.value
    const endDate = this.endDateTarget.value
    const selectedUsers = this.getSelectedUsers()
    
    // Clear any existing warnings first
    this.clearUserWarnings()
    
    if (!startDate || !endDate || selectedUsers.length === 0) {
      this.hideConflicts()
      return
    }
    
    // Validate dates
    if (new Date(startDate) > new Date(endDate)) {
      this.hideConflicts()
      return
    }
    
    // Make API call to check for conflicts
    try {
      const params = new URLSearchParams()
      params.append('start_date', startDate)
      params.append('end_date', endDate)
      selectedUsers.forEach(userId => {
        params.append('user_ids[]', userId)
      })
      
      const url = `/projects/${this.projectSlugValue}/milestones/${this.milestoneIdValue}/check_conflicts?${params}`
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        this.displayConflicts(data.conflicts)
      }
    } catch (error) {
      console.error('Error checking conflicts:', error)
    }
  }
  
  getSelectedUsers() {
    return this.userCheckboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)
  }
  
  displayConflicts(conflicts) {
    if (conflicts.length === 0) {
      this.hideConflicts()
      return
    }
    
    // Add warning icons next to users with conflicts
    conflicts.forEach(conflict => {
      this.addUserWarning(conflict.user_id, conflict)
    })
    
    // Update the main conflict warnings section
    this.conflictListTarget.innerHTML = ''
    conflicts.forEach(conflict => {
      const li = document.createElement('li')
      li.textContent = `${conflict.user_name} is already assigned to ${conflict.conflicting_with} (${conflict.date_range})`
      this.conflictListTarget.appendChild(li)
    })
    
    this.conflictWarningsTarget.classList.remove('hidden')
  }
  
  addUserWarning(userId, conflict) {
    // Find the checkbox for this user
    const checkbox = this.userCheckboxTargets.find(cb => cb.value === userId.toString())
    if (!checkbox) return
    
    // Find the label that contains the checkbox
    const label = checkbox.closest('label')
    if (!label) return
    
    // Remove any existing warning icon
    const existingWarning = label.querySelector('.conflict-warning')
    if (existingWarning) {
      existingWarning.remove()
    }
    
    // Create and add warning icon with tooltip
    const warningIcon = document.createElement('div')
    warningIcon.className = 'conflict-warning tooltip tooltip-right ml-auto'
    warningIcon.setAttribute('data-tip', `Already assigned to ${conflict.conflicting_with} (${conflict.date_range})`)
    warningIcon.innerHTML = '<iconify-icon icon="ph:warning" class="text-warning text-lg"></iconify-icon>'
    
    label.appendChild(warningIcon)
  }
  
  clearUserWarnings() {
    // Remove all existing warning icons
    const warnings = this.element.querySelectorAll('.conflict-warning')
    warnings.forEach(warning => warning.remove())
  }
  
  hideConflicts() {
    this.clearUserWarnings()
    if (this.hasConflictWarningsTarget) {
      this.conflictWarningsTarget.classList.add('hidden')
    }
  }
  
  submit(event) {
    // Allow form submission
    // Conflicts will be handled server-side
  }
}

